// ── BCHNostr — business verification lookups ──────────────────────
//
// Reads the paid-verification state for a business (kind 30499 author). The
// GRANT is author-filtered to the trusted issuer set — that is the trust
// boundary. The business's own CLAIM is only used to surface a PENDING state
// while a payment awaits its grant. Also reads the admin-configurable fee.
//
// See lib/businessVerify.ts for the model. Never trust a grant from a
// non-issuer; never derive "accepting BCH" from any of this.

import { useMemo } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  BUSINESS_VERIFY_KIND, BUSINESS_VERIFY_GRANT_TAG, BUSINESS_VERIFY_CLAIM_D,
  BUSINESS_VERIFY_CONFIG_D, VERIFY_GRANT_ISSUERS,
  verifyGrantDTag, parseVerificationGrant, parseVerifyClaim,
  parseVerifyFeeConfig, computeVerificationState,
  DEFAULT_BUSINESS_VERIFY_FEE_USD,
  type VerificationState,
} from '../lib/businessVerify';

export interface UseBusinessVerificationResult extends VerificationState {
  loading: boolean;
  refetch: () => void;
}

/**
 * Verification state for a single business (its 30499 author pubkey).
 *
 * Two author-scoped queries:
 *   · the issuer-signed GRANT (authors = trusted issuers), and
 *   · the business's own CLAIM (author = the business).
 * The grant is authoritative; the claim only yields PENDING.
 */
export function useBusinessVerification(
  businessPubkey: string | undefined,
): UseBusinessVerificationResult {
  const enabled = !!businessPubkey && /^[0-9a-f]{64}$/i.test(businessPubkey);
  const biz = enabled ? businessPubkey!.toLowerCase() : '';

  const { events: grantEvents, loading: grantLoading, refetch: refetchGrant } = useNostrQuery({
    filters: enabled
      ? [{
          kinds: [BUSINESS_VERIFY_KIND],
          authors: VERIFY_GRANT_ISSUERS,
          '#d': [verifyGrantDTag(biz)],
          limit: 10,
        }]
      : [],
    enabled,
    allRelays: true,
  });

  const { events: claimEvents, loading: claimLoading, refetch: refetchClaim } = useNostrQuery({
    filters: enabled
      ? [{
          kinds: [BUSINESS_VERIFY_KIND],
          authors: [biz],
          '#d': [BUSINESS_VERIFY_CLAIM_D],
          limit: 5,
        }]
      : [],
    enabled,
    allRelays: true,
  });

  const state = useMemo<VerificationState>(() => {
    // Newest valid grant.
    let grant = null;
    for (const ev of grantEvents) {
      const g = parseVerificationGrant(ev);
      if (!g || g.businessPubkey !== biz) continue;
      if (!grant || g.issuedAt > grant.issuedAt) grant = g;
    }
    // Newest claim.
    let claim = null;
    for (const ev of claimEvents) {
      const c = parseVerifyClaim(ev);
      if (!c) continue;
      if (!claim || c.claimedAt > claim.claimedAt) claim = c;
    }
    return computeVerificationState(grant, claim);
  }, [grantEvents, claimEvents, biz]);

  return {
    ...state,
    loading: grantLoading || claimLoading,
    refetch: () => { refetchGrant(); refetchClaim(); },
  };
}

/**
 * The current, admin-configurable verification fee in USD. Falls back to the
 * default. The config event is honoured only from the operator (enforced in
 * parseVerifyFeeConfig), so this cannot be lowered by an untrusted key.
 */
export function useBusinessVerifyFeeUsd(): { feeUsd: number; loading: boolean } {
  const { events, loading } = useNostrQuery({
    filters: [{
      kinds: [BUSINESS_VERIFY_KIND],
      authors: VERIFY_GRANT_ISSUERS,
      '#d': [BUSINESS_VERIFY_CONFIG_D],
      limit: 5,
    }],
    allRelays: true,
  });

  const feeUsd = useMemo(() => {
    let best: number | null = null;
    let bestAt = 0;
    for (const ev of events) {
      const cfg = parseVerifyFeeConfig(ev);
      if (cfg && ev.created_at > bestAt) { best = cfg.feeUsd; bestAt = ev.created_at; }
    }
    return best ?? DEFAULT_BUSINESS_VERIFY_FEE_USD;
  }, [events]);

  return { feeUsd, loading };
}

/**
 * All verification grants across all businesses, newest per business. Used by
 * the directory to badge verified merchants and by the admin panel. Grants
 * are author-filtered to the trusted issuer set.
 */
export function useAllBusinessVerifications(): {
  grants: ReturnType<typeof parseVerificationGrant>[];
  loading: boolean;
  refetch: () => void;
} {
  const { events, loading, refetch } = useNostrQuery({
    filters: [{
      kinds: [BUSINESS_VERIFY_KIND],
      authors: VERIFY_GRANT_ISSUERS,
      '#t': [BUSINESS_VERIFY_GRANT_TAG],
      limit: 1000,
    }],
    allRelays: true,
  });

  const grants = useMemo(() => {
    const byBiz = new Map<string, NonNullable<ReturnType<typeof parseVerificationGrant>>>();
    for (const ev of events) {
      const g = parseVerificationGrant(ev);
      if (!g) continue;
      const existing = byBiz.get(g.businessPubkey);
      if (!existing || g.issuedAt > existing.issuedAt) byBiz.set(g.businessPubkey, g);
    }
    return [...byBiz.values()];
  }, [events]);

  return { grants, loading, refetch };
}
